import React from 'react';
import { ArrowUpCircle, Loader2 } from 'lucide-react';
import { usePlan, useReportUpsellShown, useUpgradeToTeam, type UpsellSurface } from '@redbax/shared';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

interface UpgradePromptProps {
  surface: UpsellSurface;
  title: string;
  description: string;
  /** Inline sits in the flow of a page; panel fills the space a team-only feature would occupy. */
  variant?: 'inline' | 'panel';
  className?: string;
}

/**
 * The free tier's one shared upsell surface (epic #121, story #135).
 *
 * ONE COMPONENT RATHER THAN A PROMPT PER PAGE, because every one of these has to report itself to the
 * funnel (story #137) and report the click that led to an upgrade. Written per page, the measurement
 * would be right in the places someone remembered and quietly absent elsewhere - and "which surface
 * precedes an upgrade" would then be answered with whichever surface happened to be instrumented.
 *
 * Renders NOTHING for a team. The free tier must be invisible to a paying customer, and a component
 * that decides that itself cannot be forgotten at a call site.
 */
export function UpgradePrompt({
  surface,
  title,
  description,
  variant = 'inline',
  className,
}: UpgradePromptProps) {
  const { isPersonal } = usePlan();
  const { state, upgrade } = useUpgradeToTeam(surface);

  // Reported only while actually shown, so the count means "a free user saw this".
  useReportUpsellShown(surface, isPersonal);

  if (!isPersonal) return null;

  const busy = state === 'upgrading' || state === 'done';

  return (
    <div
      className={cn(
        'rounded-lg border border-red-accent/30 bg-red-subtle/30 p-4',
        variant === 'panel' && 'flex flex-col items-center gap-2 py-12 text-center',
        className,
      )}
    >
      <div className={cn('flex items-start gap-3', variant === 'panel' && 'flex-col items-center')}>
        <ArrowUpCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-accent" />
        <div className={cn(variant === 'panel' && 'max-w-md')}>
          <h3 className="text-sm font-medium text-text-primary">{title}</h3>
          <p className="mt-1 text-sm text-text-secondary">{description}</p>

          {state === 'conflict' && (
            <p className="mt-2 text-sm text-text-secondary">
              An upgrade is already in progress. This page will update when it finishes.
            </p>
          )}
          {state === 'error' && (
            <p className="mt-2 text-sm text-red-accent">
              We couldn't start the upgrade. Nothing has changed — please try again.
            </p>
          )}

          <Button className="mt-3" onClick={upgrade} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {busy ? 'Upgrading…' : 'Upgrade to a team'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fills a route a personal organization cannot use (story #134).
 *
 * The route stays REGISTERED and renders this instead of being removed. A removed route is a 404 on a
 * bookmark or a shared link, which reads as a broken product rather than a feature they do not have
 * yet — and this way the page can say what the feature is and offer it.
 */
export function TeamOnly({ children, feature }: { children: React.ReactNode; feature: string }) {
  const { isPersonal } = usePlan();

  if (!isPersonal) return <>{children}</>;

  return (
    <UpgradePrompt
      surface="locked_feature"
      variant="panel"
      title={`${feature} is part of a team plan`}
      description={
        `Your free account is protected by Redbax's default detection policy, which is already ` +
        `running on your devices. Upgrading to a team lets you tailor ${feature.toLowerCase()} ` +
        `and invite the people you work with.`
      }
    />
  );
}
